import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import {
  calculateWindowArea,
  calculateFrameArea,
  calculateGlassEdge,
  calculateMuntinLength,
  extractFrameWidths,
  WindowDimensions
} from '../utils/calculation';

/* ===================== INTERFACES ===================== */
export enum mutin_spcer {
  none,
  fixed_addition
};
export interface WindowPayload {
  frame: {
    type: number;
    uf: number;
    visibleWidth: number;
    system: {
      manufacturerId: string;
      systemId: number;
    };
  };
  glass: {
    id: string;
    glazingCount: number;
    ug: number;
    interiorGlassThickness: number;
    exteriorGlassThickness: number;
    cavities: number[];
    name: string;
    type: string;
    thickness: number;
    uValue: number;
    gValue: number;
    lightTransmittance: number;
    coating: string;
    manufacturer: number;
    isLowE: boolean;
    region: number[];
  };
  spacer: {
    id: string;
    name: string;
    isManual: boolean;
    frsi: number;
    psi: number;
    img: string;
    repfrsi: Array<{
      category: number;
      values: number[];
    }>;
    reppsi: Array<{
      category: number;
      values: number[];
    }>;
  };
  spacerExpert: {
    sealant: {
      conductivity: number;
      size: number;
    };
    desiccant: {
      id: string;
    };
  } | null;
  muntin: {
    psi: number;
    type: {
      id: number;
      name: string;
    };
    shape: {
      id: number;
      countHorizontal: number;
      countVertical: number;
    };
    spacer: number;
  };
  windowType: number;
  dimensions: {
    a: number;
    b: number;
    c: number;
  };
  windowArea: number;
  frameArea: number;
  glassArea: number;
  glassEdge: number;
  muntinLength: number;
  includeCorrectionPsiForCavities: boolean;
}

/* ===================== SERVICE ===================== */

@Injectable({ providedIn: 'root' })
export class WindowPayloadBuilderService {
  constructor(private storageService: StorageService) { }

  /**
   * Build complete window payloads for both window1 and window2
   */
  async buildWindowPayloads(customData?: any): Promise<{
    window1: WindowPayload;
    window2: WindowPayload;
  }> {
    const w1Project = await this.getProjectData(false);
    const w2Project = await this.getProjectData(true);

    return {
      window1: this.mapProjectToWindow(w1Project, customData),
      window2: this.mapProjectToWindow(w2Project, customData)
    };
  }

  /**
   * Get project data from storage
   */
  private async getProjectData(isComparison: boolean): Promise<any> {
    const data = isComparison
      ? await this.storageService.getComparedProject()
      : await this.storageService.getProject();

    return data ? JSON.parse(data) : this.getDefaultProject();
  }

  /**
   * Map project data to window payload structure
   * Based on StorageService storage format
   */
  private mapProjectToWindow(project: any, customData?: any): WindowPayload {
    console.log(customData,project.windowtype)
    // Storage uses: windowframe, windowglas, spacer, muntin, windowtype
    const frame = project.windowframe || {};
    const glass = project.windowglas || {};
    const spacer = project.spacer || {};
    const spacerExpert = project.spacerExpert || null;
    const muntin = project.muntin || {};
    const windowType = customData?.windowtype?.id ?? project.windowtype ?? {};

    // Get dimensions - stored as dimensions.a, dimensions.b, dimensions.c
    const dimensions: WindowDimensions = {
      a: parseFloat(customData?.dimensions?.dimA ?? project?.dimensions?.a ?? 0),
      b: parseFloat(customData?.dimensions?.dimB ?? project?.dimensions?.b ?? 0),
      c: parseFloat(customData?.dimensions?.dimC ?? project?.dimensions?.c ?? 0)
    };

    const windowTypeId = parseInt(windowType || 1);
    const frameWidths = extractFrameWidths(frame);

    // Calculate all areas and lengths using calculation.ts
    const windowArea = calculateWindowArea(windowTypeId, dimensions);
    const frameArea = calculateFrameArea(windowTypeId, dimensions, frameWidths);
    const glassArea = windowArea - frameArea;
    const glassEdge = calculateGlassEdge(windowTypeId, dimensions, frameWidths);

    const muntinType = { id: parseInt(muntin.type?.id || 0) };
    const muntinShape = {
      id: parseInt(muntin.shape?.id || 0),
      countHorizontal: parseInt(muntin.shape?.countHorizontal || 0),
      countVertical: parseInt(muntin.shape?.countVertical || 0)
    };
    const muntinLength = calculateMuntinLength(
      windowTypeId,
      dimensions,
      frameWidths,
      muntinType,
      muntinShape
    );

    return {
      frame: {
        type: this.mapFrameType(frame.type),
        uf: parseFloat(frame.uf || 0),
        visibleWidth: parseFloat(frame.visibleWidth || frame.w0 || 0),
        system: {
          manufacturerId: (frame.system?.manufacturerId || '').toString(),
          systemId: parseInt(frame.system?.systemId || 0)
        }
      },
      glass: {
        id: String(glass.id || ''),
        glazingCount: parseInt(glass.glazingCount || 0),
        ug: parseFloat(glass.ug || 0),
        interiorGlassThickness: parseFloat(glass.interiorGlassThickness || 0),
        exteriorGlassThickness: parseFloat(glass.exteriorGlassThickness || 0),
        cavities: this.parseCavities(glass.cavities),
        name: glass.product || glass.name || '',
        type: glass.type || '',
        thickness: parseFloat(glass.thickness || 0),
        uValue: parseFloat(glass.ug || 0),
        gValue: parseFloat(glass.gValue || 0), // stored as gValue
        lightTransmittance: parseFloat(glass.lightTransmittance || 0), // stored as light_trans
        coating: glass.coating || '',
        manufacturer: parseInt(glass.manufacturer || 0),
        isLowE: this.checkIsLowE(glass),
        region: this.parseRegion(glass.region)
      },
      spacer: {
        id: (spacer.id || '').toString(),
        name: spacer.name || '',
        isManual: spacer.id == 0 ? true : false,
        frsi: parseFloat(spacer.frsi || 0),
        psi: parseFloat(spacer.psi || 0),
        img: spacer.img,
        repfrsi: this.parseRepValues(spacer.repfrsi),
        reppsi: this.parseRepValues(spacer.reppsi)
      },
      spacerExpert: spacerExpert
        ? {
          sealant: {
            conductivity: parseFloat(spacerExpert.sealant?.conductivity || 0),
            size: parseFloat(spacerExpert.sealant?.size || 0)
          },
          desiccant: {
            id: (spacerExpert.desiccant?.id || '').toString()
          }
        }
        : null,
      muntin: {
        psi: parseFloat(muntin.psi || 0),
        type: {
          id: muntinType.id,
          name: muntin.type?.name || ''
        },
        shape: {
          id: muntinShape.id,
          countHorizontal: muntinShape.countHorizontal,
          countVertical: muntinShape.countVertical
        },
        spacer: mutin_spcer.none
      },
      windowType: windowTypeId,
      dimensions: {
        a: dimensions.a,
        b: dimensions.b,
        c: dimensions.c
      },
      windowArea,
      frameArea,
      glassArea,
      glassEdge,
      muntinLength,
      // includeCorrectionPsiForCavities: project.includeCorrectionPsiForCavities !== false
      includeCorrectionPsiForCavities: project?.glassCorrectionFactor ?? false
    };
  }

  /* ===================== HELPER FUNCTIONS ===================== */

  /**
   * Map frame type string constant to numeric value
   * Based on your Constants (FRAME_TYPE_PVC, FRAME_TYPE_ALUMETAL, etc.)
   */
  private mapFrameType(typeString: string): number {
    const typeMap: { [key: string]: number } = {
      'FRAME_TYPE_PVC': 1,
      'FRAME_TYPE_WOOD': 0,
      'FRAME_TYPE_ALU_METAL': 3,
      'FRAME_TYPE_ALU_WOOD': 2,
      'FRAME_TYPE_STEEL': 4
    };
    return typeMap[typeString] || 0;
  }

  /**
   * Check if glass is Low-E based on available data
   */
  private checkIsLowE(glass: any): boolean {
    // Check explicit flag
    if (glass.isLowE === true || glass.is_low_e === true) {
      return true;
    }
    // Check coating
    if (glass.coating?.toLowerCase().includes('low-e')) {
      return true;
    }
    // Check type
    if (glass.type?.toLowerCase().includes('low-e')) {
      return true;
    }
    // Check product name
    if (glass.name?.toLowerCase().includes('eclaz') ||
      glass.name?.toLowerCase().includes('low-e')) {
      return true;
    }
    return false;
  }

  /**
   * Parse region - stored as array like [0, -10, -20, -44]
   */
  private parseRegion(region: any): number[] {
    if (!region) {
      return [];
    }

    if (Array.isArray(region)) {
      return region.map(Number).filter(n => !isNaN(n));
    }

    if (typeof region === 'string') {
      return region
        .split(',')
        .map(value => Number(value.trim()))
        .filter(n => !isNaN(n));
    }

    return [];
  }


  /**
   * Parse cavities - stored as array like [14, 14] or [16]
   */
  private parseCavities(cavities: any): number[] {
    if (!cavities) return [];
    if (Array.isArray(cavities)) {
      return cavities.map(c => parseFloat(c || 0));
    }
    if (typeof cavities === 'string') {
      try {
        const parsed = JSON.parse(cavities);
        return Array.isArray(parsed) ? parsed.map(c => parseFloat(c || 0)) : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  /**
   * Parse repfrsi/reppsi values
   */
  private parseRepValues(repData: any): Array<{ category: number; values: number[] }> {
    if (!repData) return [];

    if (Array.isArray(repData)) {
      return repData.map((item, index) => {
        // Map category string to number
        const categoryNum = this.mapCategoryToNumber(item.category);

        // Extract values from the nested structure
        let values: number[] = [];

        if (item.value && Array.isArray(item.value)) {
          // Chrome format: [{frsi: -0.06889}, {frsi: 0.73333}, ...]
          // or [{psi: -0.01144}, {psi: 0.04332}, ...]
          values = item.value.map((v: any) => {
            if (typeof v === 'object') {
              return parseFloat(v.frsi || v.psi || 0);
            }
            return parseFloat(v || 0);
          });
        } else if (item.values && Array.isArray(item.values)) {
          // Already in correct format
          values = item.values.map((v: any) => parseFloat(v || 0));
        }

        return {
          category: categoryNum,
          values: values
        };
      });
    }

    if (typeof repData === 'string') {
      try {
        const parsed = JSON.parse(repData);
        return this.parseRepValues(parsed);
      } catch {
        return [];
      }
    }

    return [];
  }

  /**
 * Map category string to number
 */
  private mapCategoryToNumber(category: any): number {
    if (typeof category === 'number') return category;

    const categoryMap: { [key: string]: number } = {
      'wood': 0,
      'pvc': 1,
      'alu-wood': 2,
      'aluwood': 2,
      'alu': 3,
      'alumetal': 3
    };

    const categoryStr = String(category || '').toLowerCase();
    return categoryMap[categoryStr] !== undefined ? categoryMap[categoryStr] : 0;
  }


  /**
   * Get default project structure matching StorageService format
   */
  private getDefaultProject(): any {
    return {
      windowframe: {},
      windowglas: {},
      spacer: {},
      muntin: {},
      windowtype: 1,
      dimensions: { a: 0, b: 0, c: 0 }
    };
  }
}
